import { AppShell, Badge, Burger, Button, Group, NavLink, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAdjustments,
  IconBuildingFactory2,
  IconCalendarTime,
  IconClipboardList,
  IconCpu,
  IconDashboard,
  IconGauge,
  IconListTree,
  IconPackage,
  IconRouter,
  IconServerCog,
  IconSettingsAutomation,
  IconUsers,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { hasPermission } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useLiveUpdates } from "../live/useLiveUpdates";

const NAV = [
  { to: "/", label: "Dashboard", icon: IconDashboard, permission: "machine:read" },
  { to: "/machines", label: "Machines", icon: IconSettingsAutomation, permission: "machine:read" },
  { to: "/factories", label: "Factories", icon: IconBuildingFactory2, permission: "factory:read" },
  { to: "/gateways", label: "Gateways", icon: IconRouter, permission: "gateway:read" },
  { to: "/users", label: "Users", icon: IconUsers, permission: "user:read" },
  { to: "/audit", label: "Audit log", icon: IconClipboardList, permission: "audit:read" },
];
// Company configuration: everything a new customer sets up without code changes.
const CONFIG_NAV = [
  { to: "/config/machine-types", label: "Machine types", icon: IconCpu, permission: "machine:read" },
  { to: "/config/shifts", label: "Shifts & calendar", icon: IconCalendarTime, permission: "factory:read" },
  { to: "/config/products", label: "Products & jobs", icon: IconPackage, permission: "production:read" },
  { to: "/config/reasons", label: "Reason codes", icon: IconListTree, permission: "downtime:read" },
  { to: "/config/kpi", label: "KPI settings", icon: IconGauge, permission: "production:read" },
];

export function Layout({ children }: { children: ReactNode }) {
  const { me, logout, actingCompany, actAs } = useAuth();
  const [opened, { toggle }] = useDisclosure();
  const { pathname } = useLocation();
  const tenantUser = me?.company_id !== null;
  const live = useLiveUpdates(!!me && tenantUser);

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 230, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>IIoT Platform</Title>
            {me?.company_name && <Badge variant="light" visibleFrom="xs">{me.company_name}</Badge>}
          </Group>
          <Group gap="sm" wrap="nowrap">
            {actingCompany && (
              <Button size="xs" variant="light" color="orange" onClick={() => void actAs(null)}>
                Leave {actingCompany.name}
              </Button>
            )}
            {tenantUser && (
              <Badge variant="dot" color={live === "live" ? "green" : live === "connecting" ? "yellow" : "gray"}
                title="Live updates: changes appear without reloading">
                {live === "live" ? "live" : live}
              </Badge>
            )}
            <Text size="sm" visibleFrom="sm">{me?.full_name}</Text>
            <Button size="xs" variant="default" onClick={() => void logout()}>Sign out</Button>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="xs">
        <Stack gap={2}>
          {tenantUser &&
            NAV.filter((n) => hasPermission(me, n.permission)).map((n) => (
              <NavLink key={n.to} component={Link} to={n.to} label={n.label} leftSection={<n.icon size={18} />}
                active={n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)} onClick={toggle} />
            ))}
          {tenantUser && (
            <NavLink label="Configuration" leftSection={<IconAdjustments size={18} />} defaultOpened={pathname.startsWith("/config")}>
              {CONFIG_NAV.filter((n) => hasPermission(me, n.permission)).map((n) => (
                <NavLink key={n.to} component={Link} to={n.to} label={n.label} leftSection={<n.icon size={16} />}
                  active={pathname.startsWith(n.to)} onClick={toggle} />
              ))}
            </NavLink>
          )}
          {me?.is_platform_admin && (
            <NavLink component={Link} to={tenantUser ? "/platform" : "/"} label="Platform admin"
              leftSection={<IconServerCog size={18} />} active={pathname === "/platform" || !tenantUser} />
          )}
        </Stack>
      </AppShell.Navbar>
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
